import isEqual from "lodash/isEqual";
import mapboxgl from "mapbox-gl";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import {
  jitterMarkers,
  createGeoJSONFromMarkers,
  createSpiderLines,
  fitMapToFeatures,
  addResetControl,
  addFilterControl,
} from "ui/map/utils/action";
import {
  addSources,
  addLayers,
  addMapEventListeners,
} from "ui/map/utils/layer";

mapboxgl.accessToken =
  "pk.eyJ1IjoidmlzdGFyLW1lZGlhIiwiYSI6ImNsa3ZpeDFjYzBuYjMzZm84ZzZ4YWFzZTYi" +
  "fQ.REmBHbTOgq0gstmGG7HpDw";

const MapboxMap = ({
  deviceStatusOnline,
  markers,
  maxZoom = 20,
  minZoom = 1,
}) => {
  const currentMarkersRef = useRef([]);
  const lastFeaturesRef = useRef([]);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    const map = new mapboxgl.Map({
      center: [-75.17917, 39.965033],
      container: mapContainerRef.current,
      maxBounds: [
        [-360, -85],
        [360, 85],
      ],
      maxZoom,
      minZoom,
      projection: "mercator",
      style: "mapbox://styles/vistar-media/clsm29t0t03zr01p2h7qfb5f2",
      zoom: 8,
    });

    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl(), "top-left");

    map.on("load", () => {
      map.once("idle", () => {
        addSources(map);
        addLayers(map);
        addMapEventListeners(map);
        addResetControl(map, () => {
          if (lastFeaturesRef.current.length > 0) {
            fitMapToFeatures(map, lastFeaturesRef.current, maxZoom);
          }
        });
        addFilterControl(map, (filterValue) => {
          filterMarkers(filterValue);
        });

        if (markers.length) {
          refreshMapData(markers);
        }
      });
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (mapRef.current && markers.length > 0) {
      const prevMarkers = currentMarkersRef.current;
      if (isEqual(markers, prevMarkers)) {
        // No meaningful change; skip update
        return;
      }
      currentMarkersRef.current = markers;
      refreshMapData();
    }
  }, [markers]);

  const refreshMapData = (filterValue = "all") => {
    /* Update the map data with the current markers
     * - Filters markers based on the provided filterValue
     * - Updates the map with the new markers and spider lines
     * @param {string} filterValue - The filter value to apply ("all", "online", "offline")
     */
    currentMarkersRef.current = markers;
    filterMarkers(filterValue, true);
  };

  const filterMarkers = (filterValue = "all", fitToFeature = false) => {
    /* Filter the markers based on the provided filterValue
     * - Updates the map data with the filtered markers
     * @param {string} filterValue - The filter value to apply ("all", "online", "offline")
     * @param {boolean} fitToFeature - Whether to fit the map to the filtered features
     */

    const map = mapRef.current;
    if (!map) {
      return;
    }

    let filteredMarkers = currentMarkersRef.current;

    if (filterValue === "online") {
      filteredMarkers = filteredMarkers.filter(
        (m) => m.deviceStatus?.color === "green",
      );
    } else if (filterValue === "offline") {
      filteredMarkers = filteredMarkers.filter(
        (m) => m.deviceStatus?.color === "red",
      );
    }

    const jitteredFiltered = jitterMarkers(filteredMarkers);
    const geojson = createGeoJSONFromMarkers(jitteredFiltered);
    const spiderLines = createSpiderLines(jitteredFiltered);

    lastFeaturesRef.current = geojson.features;

    if (map.getSource("devices") && map.getSource("spider-lines")) {
      map.getSource("devices").setData(geojson);
      map.getSource("spider-lines").setData(spiderLines);
    }

    if (fitToFeature) {
      fitMapToFeatures(map, geojson.features, maxZoom);
    }
  };

  const offlineClass = deviceStatusOnline === false ? "offline" : "";

  return (
    <div
      ref={mapContainerRef}
      className={`map-container online-component ${offlineClass}`}
    />
  );
};

MapboxMap.propTypes = {
  deviceStatusOnline: PropTypes.bool,
  markers: PropTypes.array.isRequired,
  maxZoom: PropTypes.number,
  minZoom: PropTypes.number,
};

export { MapboxMap };
